import React, { useEffect, useState } from "react";
import {
  Checkbox,
  DropdownListOrTextInput,
  Loader,
  BaseConfirmationModal,
  StandardButton,
  SubmitButton,
} from "../../../components";
import { COLOURS } from "../../../shared";
import { CircleHelp } from "lucide-react";
import styled from "@emotion/styled";
import { useSmokeballApp } from "../../../shared";
import { useDataOrigins, useExportReasons } from "../../../hooks";
import log from "../../../shared/utils/log";

type DataOrigin = {
  code: string;
  name: string;
  enabled: boolean;
  regions: string[];
};

type ExportReason = {
  name: string;
  enabled: boolean;
  regions: string[];
};

const DropdownWrapper = styled.div<{ isFirmOwnerOrInternalUser: boolean }>`
  margin: 0.4rem 0 0
    ${({ isFirmOwnerOrInternalUser }) =>
      isFirmOwnerOrInternalUser ? "3.6rem" : "0rem"};
  width: ${({ isFirmOwnerOrInternalUser }) =>
    isFirmOwnerOrInternalUser ? "calc(100% - 4.2rem)" : "100%"};
`;

const ModalContent = styled.div`
  margin-bottom: 2rem;  
`;

const ModalDescriptionText = styled.span`
  font-size: ${(props) => props.theme.fontSize.emphasis};
  font-weight: 400;
  color: ${COLOURS.modalParagraphColor};
  margin-left: 0;
  margin-bottom: 1.2rem;
  display: block;
  line-height: 1.4;
`;

export const ImportOrExportTypeSelectionModal: React.FC<{
  closeModal: () => void;
  onConfirm: (result: {
    actionType: string;
    dataOrigin?: string;
    exportReason?: string;
  }) => void;
  onError: () => void;
  isImportDisabled: boolean;
  isFirmOwnerOrInternalUser: boolean;
}> = ({
  closeModal,
  onConfirm,
  onError,
  isImportDisabled,
  isFirmOwnerOrInternalUser,
}) => {
  const { auth, context } = useSmokeballApp();
  const accountId = context?.firmId || "";
  const token = auth?.token;
  log("Account Id: ", accountId);

  // Data Origins
  const {
    data: fetchedOrigins = [],
    isLoading: isLoadingDataOrigins,
    isError: isErrorDataOrigins,
    error: errorDataOrigins,
  } = useDataOrigins({ accountId, token }) as {
    data: DataOrigin[];
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };

  const dataOriginOptions = fetchedOrigins
    .filter((origin) => origin.enabled)
    .map((origin) => ({
      label: origin.name,
      value: origin.code,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Export Reasons
  const {
    data: fetchedExportReasons = [],
    isLoading: isLoadingExportReasons,
    isError: isErrorExportReasons,
    error: errorExportReasons,
  } = useExportReasons({ accountId, token }) as {
    data: ExportReason[];
    isLoading: boolean;
    isError: boolean;
    error: unknown;
  };

  const exportReasonOptions = fetchedExportReasons
    .filter((reason) => reason.enabled)
    .map((reason) => ({
      label: reason.name,
      value: reason.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  log("exportReasonOptions:", exportReasonOptions);

  const [selectedActionIndex, setSelectedActionIndex] = useState<number | null>(
    isFirmOwnerOrInternalUser ? null : 0,
  );

  // Data Origin states
  const [dataOrigin, setDataOrigin] = useState("");
  const [dataOriginDisplayLabel, setDataOriginDisplayLabel] = useState("");
  const [dataOriginCustomConfirmed, setDataOriginCustomConfirmed] =
    useState(false);

  // Export Reason states
  const [exportReason, setExportReason] = useState("");
  const [exportReasonDisplayLabel, setExportReasonDisplayLabel] = useState("");
  const [exportReasonCustomConfirmed, setExportReasonCustomConfirmed] =
    useState(false);

  const [hasErrorTriggered, setHasErrorTriggered] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const isImport = selectedActionIndex === 0;
  const isExport = selectedActionIndex === 1;

  const handleChange = (index: number) => {
    setSelectedActionIndex(index);
    setDataOrigin("");
    setDataOriginDisplayLabel("");
    setDataOriginCustomConfirmed(false);
    setExportReason("");
    setExportReasonDisplayLabel("");
    setExportReasonCustomConfirmed(false);
  };

  const isDataOriginValid = () => {
    return (
      !isImport ||
      dataOriginOptions.some((opt) => opt.value === dataOrigin) ||
      (dataOrigin && dataOriginCustomConfirmed)
    );
  };

  const isExportReasonValid = () => {
    return (
      !isExport ||
      exportReasonOptions.some((opt) => opt.value === exportReason) ||
      (exportReason && exportReasonCustomConfirmed)
    );
  };

  const isSubmitButtonDisabled =
    selectedActionIndex === null ||
    (isImport && !isDataOriginValid()) ||
    (isExport && !isExportReasonValid());

  const handleSubmit = async () => {
    if (selectedActionIndex === null) {
      console.error("No option selected for import/export.");
      return;
    }

    setIsLoading(true);
    try {
      const actionType = isImport ? "import" : "export";
      onConfirm({
        actionType,
        dataOrigin: isImport ? dataOrigin.trim() : undefined,
        exportReason: isExport ? exportReason.trim() : undefined,
      });
      closeModal();
    } catch (error) {
      console.error("Error during the process:", error);
      onError();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isErrorDataOrigins) {
      log("isErrorDataOrigins:", errorDataOrigins);
      setHasErrorTriggered(true);
      onError();
    }
    if (isErrorExportReasons) {
      log("isErrorExportReasons:", errorExportReasons);
      setHasErrorTriggered(true);
      onError();
    }
    if (!isFirmOwnerOrInternalUser) {
      // lock to data origin only question view
      setSelectedActionIndex(0);

      // make sure export state never interferes
      setExportReason("");
      setExportReasonDisplayLabel("");
      setExportReasonCustomConfirmed(false);
    }
  }, [
    isErrorDataOrigins,
    isErrorExportReasons,
    errorDataOrigins,
    errorExportReasons,
    onError,
    isFirmOwnerOrInternalUser,
  ]);
  if (hasErrorTriggered) {
    return null; // don’t render modal
  }

  return (
    <>
      <BaseConfirmationModal
        backgroundColor={COLOURS.matterStateSelectionIconBackground}
        icon={
          <CircleHelp
            color={COLOURS.matterStateSelectionIconColor}
            strokeWidth={2}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        }
        buttons={[
          {
            Button: StandardButton,
            label: "Cancel, go back",
            action: closeModal,
          },
          {
            Button: ({ ...props }) => (
              <SubmitButton
                {...props}
                disabled={isSubmitButtonDisabled}
                isSubmitButtonDisabled={isSubmitButtonDisabled}
              />
            ),
            label: "Continue",
            action: handleSubmit,
          },
        ]}
        headingText={
          isFirmOwnerOrInternalUser
            ? "Which action would you like to perform?"
            : "Which system are you coming from?"
        }
      >
        {isFirmOwnerOrInternalUser ? (
          <>
            {/* Firm owner/internal user */}
            <ModalContent>
              <Checkbox
                id="dataImportActionType"
                name="selectedActionTypeOption"
                value="import"
                checked={isImport}
                onChange={() => !isImportDisabled && handleChange(0)}
                label={
                  isImportDisabled
                    ? "Data Import (already in progress)"
                    : "Data Import"
                }
                description="Import new data into this firm. If you can't find your system in the list, type a custom value, then select the option shown."
                index={0}
                type="radio"
                clickableContainer={!isImportDisabled}
                style={{
                  cursor: isImportDisabled ? "not-allowed" : "pointer",
                  opacity: isImportDisabled ? 0.5 : 1,
                }}
                disabled={isImportDisabled}
              />

              {isImport && (
                <DropdownWrapper
                  isFirmOwnerOrInternalUser={isFirmOwnerOrInternalUser}
                >
                  <DropdownListOrTextInput
                    id="dataOriginInput"
                    placeholder="Start typing to select your source system..."
                    value={dataOriginDisplayLabel}
                    options={dataOriginOptions}
                    maxLength={50}
                    onChange={(val) => {
                      setDataOriginDisplayLabel(val);
                      setDataOrigin("");
                      setDataOriginCustomConfirmed(false);
                    }}
                    onSelect={(val, isCustom) => {
                      setDataOrigin(val);
                      const label =
                        dataOriginOptions.find((opt) => opt.value === val)
                          ?.label || val;
                      setDataOriginDisplayLabel(label);
                      setDataOriginCustomConfirmed(isCustom);
                    }}
                    mode="import"
                  />
                </DropdownWrapper>
              )}
            </ModalContent>

            <ModalContent>
              <Checkbox
                id="dataExportActionType"
                name="selectedActionTypeOption"
                value="export"
                checked={isExport}
                onChange={() => handleChange(1)}
                label="Data Export"
                description="Obtain a backup of the data in this firm. This process can take up to an hour depending on the size of your data."
                index={1}
                type="radio"
                clickableContainer={true}
              />

              {isExport && (
                <DropdownWrapper
                  isFirmOwnerOrInternalUser={isFirmOwnerOrInternalUser}
                >
                  <DropdownListOrTextInput
                    id="exportReasonInput"
                    placeholder="Start typing to select your reason for export..."
                    value={exportReasonDisplayLabel}
                    options={exportReasonOptions}
                    maxLength={100}
                    onChange={(val) => {
                      setExportReasonDisplayLabel(val);
                      setExportReason("");
                      setExportReasonCustomConfirmed(false);
                    }}
                    onSelect={(val, isCustom) => {
                      setExportReason(val);
                      const label =
                        exportReasonOptions.find((opt) => opt.value === val)
                          ?.label || val;
                      setExportReasonDisplayLabel(label);
                      setExportReasonCustomConfirmed(isCustom);
                    }}
                    mode="export"
                  />
                </DropdownWrapper>
              )}
            </ModalContent>
          </>
        ) : (
          <>
            {/* Non-owner/internal user */}
            <ModalContent>
              <ModalDescriptionText>
                If you can't find your system in the list, type a custom value,
                then select the option shown.
              </ModalDescriptionText>
              <DropdownWrapper
                isFirmOwnerOrInternalUser={isFirmOwnerOrInternalUser}
              >
                <DropdownListOrTextInput
                  id="dataOriginInput"
                  placeholder="Start typing to select your source system..."
                  value={dataOriginDisplayLabel}
                  options={dataOriginOptions}
                  maxLength={50}
                  onChange={(val) => {
                    setDataOriginDisplayLabel(val);
                    setDataOrigin("");
                    setDataOriginCustomConfirmed(false);
                  }}
                  onSelect={(val, isCustom) => {
                    setDataOrigin(val);
                    const label =
                      dataOriginOptions.find((opt) => opt.value === val)
                        ?.label || val;
                    setDataOriginDisplayLabel(label);
                    setDataOriginCustomConfirmed(isCustom);
                  }}
                  mode="import"
                />
              </DropdownWrapper>
            </ModalContent>
          </>
        )}
      </BaseConfirmationModal>

      {(isLoading || isLoadingDataOrigins || isLoadingExportReasons) && (
        <Loader />
      )}
    </>
  );
};
